import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/rightSidebar.css';

export interface CapabilityOption {
  readonly id: number;
  readonly name: string;
  readonly type?: string;
}

/** 上下文资源颜色类型 */
export type ResourceColor = 'blue' | 'green' | 'yellow' | 'purple' | 'red' | 'orange';

/** 上下文资源项 */
export interface ContextResource {
  readonly id: string;
  readonly label: string;
  readonly color: ResourceColor;
  readonly icon?: string;
}

/** 历史会话项 */
export interface ConversationHistoryItem {
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly isActive?: boolean;
  readonly hasUnread?: boolean;
  readonly isRunning?: boolean;
}

/** 历史会话分组 */
export interface HistoryGroup {
  readonly label: string;
  readonly items: readonly ConversationHistoryItem[];
}

/** RightSidebar 组件属性 */
export interface RightSidebarProps {
  /** 能力选择器是否禁用（禁用后不可切换） */
  readonly capabilityDisabled?: boolean;
  /** 助手名称 */
  readonly assistantName?: string;
  /** 传入可选模型列表时，顶部展示下拉选择 */
  readonly capabilities?: readonly CapabilityOption[];
  readonly currentCapabilityId?: number;
  readonly onSelectCapability?: (id: number) => void;
  /** 上下文资源列表 */
  readonly contextResources?: readonly ContextResource[];
  /** 历史会话分组列表 */
  readonly historyGroups?: readonly HistoryGroup[];
  /** 点击新建对话按钮回调 */
  readonly onClickNewChat?: () => void;
  /** 点击分享按钮回调 */
  readonly onClickShare?: () => void;
  /** 点击历史会话项回调 */
  readonly onSelectConversation?: (id: string, title: string) => void;
  /** 点击资源项回调 */
  readonly onSelectResource?: (id: string) => void;
  // ============ 新增属性 ============
  
  /** 
   * 侧边栏显示模式
   * - fixed: 固定显示在右侧（默认，适用于原始 AI 对话页）
   * - drawer: 抽屉弹窗模式（适用于 PLC Coder 等嵌入场景）
   */
  readonly sidebarMode?: 'fixed' | 'drawer';
  
  /** 抽屉是否打开（仅 drawer 模式有效） */
  readonly drawerOpen?: boolean;
  
  /** 关闭抽屉回调（仅 drawer 模式有效） */
  readonly onDrawerClose?: () => void;
  
  /** 是否显示上下文资源区域 */
  readonly showContextResources?: boolean;
  /** 加载更多历史会话（滚动到底部时触发） */
  readonly onLoadMoreHistory?: () => void;
  /** 是否还有更多历史数据 */
  readonly historyHasMore?: boolean;
  /** 是否正在加载更多历史数据 */
  readonly historyLoadingMore?: boolean;
}

/** 获取资源图标的 CSS 类名 */
const getIconClass = (color: ResourceColor): string => {
  const colorMap: Record<ResourceColor, string> = {
    blue: 'rs-icon-cal',
    green: 'rs-icon-excel',
    yellow: 'rs-icon-media',
    purple: 'rs-icon-purple',
    red: 'rs-icon-red',
    orange: 'rs-icon-orange'
  };
  return colorMap[color] ?? 'rs-icon-cal';
};

/** 获取资源默认图标 */
const getDefaultIcon = (color: ResourceColor): string => {
  const iconMap: Record<ResourceColor, string> = {
    blue: 'fas fa-calendar',
    green: 'fas fa-file-excel',
    yellow: 'fas fa-photo-video',
    purple: 'fas fa-cube',
    red: 'fas fa-exclamation-circle',
    orange: 'fas fa-folder'
  };
  return iconMap[color] ?? 'fas fa-file';
};

/** 默认上下文资源 */
const DEFAULT_CONTEXT_RESOURCES: readonly ContextResource[] = [
  { id: 'res_cal', label: 'Calender for work', color: 'blue', icon: 'fas fa-calendar' },
  { id: 'res_excel', label: 'Excel file for work', color: 'green', icon: 'fas fa-file-excel' },
  { id: 'res_media', label: 'My media files', color: 'yellow', icon: 'fas fa-photo-video' }
];

/** 默认历史分组 */
const DEFAULT_HISTORY_GROUPS: readonly HistoryGroup[] = [
  {
    label: 'Today',
    items: [
      { id: 'hist_1', title: 'What is UX writing an...', isActive: true },
      { id: 'hist_2', title: 'Create Ul design syst...' }
    ]
  },
  {
    label: 'Yesterday',
    items: [
      { id: 'hist_3', title: 'Understanding user flo...' }
    ]
  }
];

/**
 * 右侧边栏组件
 * 支持固定模式和抽屉模式
 */
export const RightSidebar: React.FC<RightSidebarProps> = ({
  capabilityDisabled = false,
  assistantName = 'Assistant v2.6',
  capabilities,
  currentCapabilityId,
  onSelectCapability,
  contextResources,
  historyGroups,
  onClickNewChat,
  onClickShare,
  onSelectConversation,
  onSelectResource,
  // 新增属性解构
  sidebarMode = 'fixed',
  drawerOpen = false,
  onDrawerClose,
  showContextResources = true,
  onLoadMoreHistory,
  historyHasMore = true,
  historyLoadingMore = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  // 记录各分组是否折叠，label -> collapsed
  const [collapsedGroups, setCollapsedGroups] = useState<Readonly<Record<string, boolean>>>({});
  // 底部哨兵元素，IntersectionObserver 监听它来触发加载更多
  const sentinelRef = useRef<HTMLDivElement>(null);
  // 用 ref 持有最新值，避免 observer 回调读到过期闭包，同时避免 observer 因 loading 变化被重建
  const historyHasMoreRef = useRef(historyHasMore);
  const historyLoadingMoreRef = useRef(historyLoadingMore);
  const onLoadMoreHistoryRef = useRef(onLoadMoreHistory);

  useEffect(() => { historyHasMoreRef.current = historyHasMore; }, [historyHasMore]);
  useEffect(() => { historyLoadingMoreRef.current = historyLoadingMore; }, [historyLoadingMore]);
  useEffect(() => { onLoadMoreHistoryRef.current = onLoadMoreHistory; }, [onLoadMoreHistory]);

  const hasDropdown: boolean = !!capabilities && capabilities.length > 0;

  const currentOption: CapabilityOption | undefined = useMemo(() => {
    if (!hasDropdown) {
      return undefined;
    }
    const match: CapabilityOption | undefined =
      capabilities?.find(item => item.id === currentCapabilityId) ??
      capabilities?.[0];
    return match;
  }, [capabilities, currentCapabilityId, hasDropdown]);

  const displayLabel: string =
    currentOption?.name ?? assistantName; // 优先显示选中项，否则显示默认名

  const handleToggle = useCallback((): void => {
    if (!hasDropdown) {
      return;
    }
    setOpen(prev => !prev);
  }, [hasDropdown]);

  const handleSelect = useCallback(
    (id: number): void => {
      onSelectCapability?.(id);
      setOpen(false);
    },
    [onSelectCapability]
  );

  /** 当前使用的上下文资源 */
  const currentResources = useMemo(
    () => contextResources ?? DEFAULT_CONTEXT_RESOURCES,
    [contextResources]
  );

  /** 当前使用的历史分组 */
  const currentHistoryGroups = useMemo(
    () => historyGroups ?? [],
    [historyGroups]
  );

  /** 切换分组的展开/折叠状态 */
  const handleToggleGroup = useCallback((label: string): void => {
    setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  }, []);

  /** 哨兵元素可见时加载更多（滚动到底部触发）
   * 只依赖 historyHasMore：hasMore 变为 false 时断开 observer，不因 loading 变化重建，
   * 避免 observer 重建后立即触发、造成无权限时无限重试的问题。
   */
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !historyHasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && historyHasMoreRef.current && !historyLoadingMoreRef.current) {
          onLoadMoreHistoryRef.current?.();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [historyHasMore]);

  /** 处理资源点击 */
  const handleResourceClick = useCallback(
    (id: string) => {
      onSelectResource?.(id);
    },
    [onSelectResource]
  );

  /** 处理历史会话点击 */
  const handleConversationClick = useCallback(
    (id: string, title: string) => {
      onSelectConversation?.(id, title);
      // 抽屉模式下，选择会话后自动关闭抽屉
      if (sidebarMode === 'drawer') {
        onDrawerClose?.();
      }
    },
    [onSelectConversation, sidebarMode, onDrawerClose]
  );

  /** 处理新建对话按钮点击 */
  const handleNewChatClick = useCallback(() => {
    onClickNewChat?.();
    // 抽屉模式下，新建对话后自动关闭抽屉
    if (sidebarMode === 'drawer') {
      onDrawerClose?.();
    }
  }, [onClickNewChat, sidebarMode, onDrawerClose]);

  /** 处理分享按钮点击 */
  const handleShareClick = useCallback(() => {
    onClickShare?.();
  }, [onClickShare]);

  /** 处理键盘事件 */
  const handleKeyDown = useCallback(
    (callback: () => void) => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        callback();
      }
    },
    []
  );

  const getDotClassName = (item: ConversationHistoryItem): string => {
    const classes = ['rs-history-dot'];
    
    if (item.isRunning) {
      classes.push('rs-history-dot-running');
    } else if (item.hasUnread) {
      classes.push('rs-history-dot-unread');
    } else if (item.isActive) {
      classes.push('rs-history-dot-active');
    }
    
    return classes.join(' ');
  };

  // ============ 新增：抽屉模式相关逻辑 ============

  /** 处理遮罩层点击（关闭抽屉） */
  const handleOverlayClick = useCallback((): void => {
    onDrawerClose?.();
  }, [onDrawerClose]);

  /** 阻止侧边栏内部点击事件冒泡 */
  const handleSidebarClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
  }, []);

  /** 计算容器 className */
  const containerClassName = useMemo((): string => {
    const classes = ['rs-right-sidebar'];
    
    if (sidebarMode === 'drawer') {
      classes.push('rs-drawer-mode');
      if (drawerOpen) {
        classes.push('rs-drawer-open');
      }
    }
    
    return classes.join(' ');
  }, [sidebarMode, drawerOpen]);

  /** 计算遮罩层 className */
  const overlayClassName = useMemo((): string => {
    const classes = ['rs-drawer-overlay'];
    if (drawerOpen) {
      classes.push('rs-overlay-visible');
    }
    return classes.join(' ');
  }, [drawerOpen]);

  // ============ 渲染侧边栏内容 ============
  const renderSidebarContent = (): React.ReactNode => (
    <>
      {/* 助手信息头部 */}
      <div className="rs-assistant-header" style={{ paddingBottom: '10px' }}>
        {/* 抽屉模式下显示关闭按钮 */}
        {sidebarMode === 'drawer' && (
          <button
            type="button"
            className="rs-drawer-close-btn"
            onClick={onDrawerClose}
            title="关闭"
          >
            <i className="fas fa-times" />
          </button>
        )}
        
        {hasDropdown ? (
          <div className="header-model-wrapper">
            <button
              type="button"
              className="header-model-trigger"
              onClick={handleToggle}
              disabled={capabilityDisabled}
              style={{ 
                width: '100%', 
                justifyContent: 'space-between',
                opacity: capabilityDisabled ? 0.6 : 1,
                cursor: capabilityDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fa-solid fa-snowflake" style={{ color: '#000' }} />
                <span className="header-model-name" style={{ color: '#000' }}>{displayLabel}</span>
                {currentOption?.type ? (
                  <span className="header-model-tag">{currentOption.type}</span>
                ) : null}
              </div>
              {!capabilityDisabled && (
                <i
                  className={`fa-solid fa-chevron-${open ? 'up' : 'down'} header-model-arrow`}
                  style={{ color: '#000', fontSize: '12px' }}
                />
              )}
            </button>

            {open && !capabilityDisabled ? (
              <div className="header-model-menu" style={{ width: '100%', left: '-9px' }}>
                {capabilities?.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    className={`header-model-item${item.id === currentOption?.id ? ' active' : ''}`}
                    onClick={(): void => handleSelect(item.id)}
                  >
                    <div className="header-model-item-main">
                      <span className="header-model-item-name">{item.name}</span>
                      {item.type ? (
                        <span className="header-model-item-type">{item.type}</span>
                      ) : null}
                    </div>
                    {item.id === currentOption?.id ? (
                      <i className="fa-solid fa-check header-model-check" />
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px'
            }}
          >
            <i className="fa-solid fa-snowflake" style={{ color: '#000' }} />
            <span style={{ fontWeight: 500, color: '#000', fontSize: '13px' }}>{displayLabel}</span>
          </div>
        )}
      </div>

      {/* 上下文资源列表（可选显示） */}
      {showContextResources && currentResources.length > 0 && (
        <div className="rs-context-section">
          {currentResources.map(resource => (
            <div
              key={resource.id}
              className="rs-list-item"
              onClick={() => handleResourceClick(resource.id)}
              onKeyDown={handleKeyDown(() => handleResourceClick(resource.id))}
              role="button"
              tabIndex={0}
            >
              <div className={`rs-item-icon ${getIconClass(resource.color)}`}>
                <i className={resource.icon ?? getDefaultIcon(resource.color)} />
              </div>
              <span>{resource.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* 新建对话按钮 */}
      <button
        type="button"
        className="rs-share-btn"
        onClick={handleNewChatClick}
      >
        <i className="fa-regular fa-comment" />
        <span>新建对话</span>
      </button>

      {/* 历史会话区 */}
      <div className="rs-history-section">
        {currentHistoryGroups.map((group, groupIndex) => {
          const isCollapsed = collapsedGroups[group.label] ?? false;
          return (
            <div key={`group_${groupIndex}`} className="rs-history-group">
              <div
                className="rs-section-title rs-section-title--clickable"
                onClick={() => handleToggleGroup(group.label)}
                onKeyDown={handleKeyDown(() => handleToggleGroup(group.label))}
                role="button"
                tabIndex={0}
                aria-expanded={!isCollapsed}
              >
                <span>{group.label}</span>
                <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'}`} />
              </div>
              {!isCollapsed && group.items.map(item => (
                <div
                  key={item.id}
                  className="rs-history-item"
                  onClick={() => handleConversationClick(item.id, item.title)}
                  onKeyDown={handleKeyDown(() => handleConversationClick(item.id, item.title))}
                  role="button"
                  tabIndex={0}
                >
                  <span className={getDotClassName(item)} />
                  <div className="rs-history-text">
                    <div className="rs-history-title-row">
                      <span className="rs-history-title">{item.title}</span>
                      {item.isRunning && (
                        <span className="rs-running-tag">生成中</span>
                      )}
                    </div>
                    {item.subtitle && (
                      <span className="rs-history-sub">{item.subtitle}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {/* 加载更多指示器 / 到底提示 */}
        {historyLoadingMore ? (
          <div className="rs-history-loading">
            <i className="fa-solid fa-circle-notch fa-spin" />
            <span>加载中...</span>
          </div>
        ) : !historyHasMore && currentHistoryGroups.length > 0 ? (
          <div className="rs-history-end">没有更多记录了</div>
        ) : null}

        {/* 滚动哨兵：进入视口时触发加载更多 */}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </>
  );

  // ============ 抽屉模式渲染 ============
  if (sidebarMode === 'drawer') {
    return (
      <>
        {/* 遮罩层 - 渲染在 .chat-main 内部 */}
        <div
          className={overlayClassName}
          onClick={handleOverlayClick}
          role="presentation"
          aria-hidden="true"
        />
        {/* 侧边栏 - 渲染在 .chat-main 内部 */}
        <aside
          className={containerClassName}
          onClick={handleSidebarClick}
          role="complementary"
          aria-label="历史对话"
        >
          {renderSidebarContent()}
        </aside>
      </>
    );
  }

  // ============ 固定模式渲染 ============
  return (
    <aside className={containerClassName} role="complementary" aria-label="历史对话">
      {renderSidebarContent()}
    </aside>
  );
};

export default RightSidebar;